import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Box, Button, Card, TextField } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ConfirmDelete from '../../../shared/components/ConfirmDelete';
import CustomDataGrid from '../../../shared/components/CustomDataGrid';
import { ERole } from '../../../shared/enums/user/ERole';
import { handleError } from '../../../shared/functions/handleError';
import { useClientColumns } from '../../../shared/hooks/useClientColumns';
import { usePagination } from '../../../shared/hooks/usePagination';
import { IClient } from '../../../shared/interfaces/clients/IClient';
import { RootState } from '../../../store/store';
import ClientFormModal from './components/ClientFormModal';
import ClientFormModalBody from './components/ClientFormModalBody';
import { AdminClientService } from './service';

const AdminClients = () => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  const [data, setData] = useState<IClient[] | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<undefined | IClient>(
    undefined,
  );
  const [isAddModalOpened, setIsAddModalOpened] = useState(false);
  const columns = useClientColumns(
    setSelectedClient,
    setShowDeleteModal,
    setIsAddModalOpened,
  );
  const {
    pagination,
    count,
    setPagination,
    totalPages,
    setTotalPages,
    handleUpdateList,
    setCount,
    changeFilterHandler,
  } = usePagination({
    perPage: 15,
  });

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedClient(undefined);
  };
  const closeActionModal = () => {
    setIsAddModalOpened(false);
    setSelectedClient(undefined);
  };
  const deleteHandler = (id?: number) => {
    !!id &&
      AdminClientService.delete(id)
        .then((response) => {
          if (response) {
            handleUpdateList();
            enqueueSnackbar(
              t('toasts.successfullyDeleted', { title: selectedClient?.name }),
              {
                variant: 'success',
              },
            );
            closeDeleteModal();
          }
        })
        .catch(handleError);
  };

  const handleSubmit = async () => {
    const isUpdate = !!selectedClient?.id;
    if (isUpdate) {
      await AdminClientService.update(selectedClient, selectedClient?.id)
        .then((response) => {
          if (response) {
            handleUpdateList();
            enqueueSnackbar(
              t('toasts.successfullyUpdated', { title: selectedClient?.name }),
              {
                variant: 'success',
              },
            );
            closeActionModal();
          }
        })
        .catch(handleError);
    } else {
      await AdminClientService.create(selectedClient as IClient)
        .then((response) => {
          if (response) {
            handleUpdateList();
            enqueueSnackbar(
              t('toasts.successfullyCreated', { title: selectedClient?.name }),
              {
                variant: 'success',
              },
            );
            closeActionModal();
          }
        })
        .catch(handleError);
    }
  };

  useEffect(() => {
    AdminClientService.get({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        setCount(response?.count);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(response.data);
      })
      .catch((err) => handleError(err));
  }, [pagination, setTotalPages, setCount]);

  return (
    <Card sx={{ marginTop: '30px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '20px',
        }}
      >
        <TextField
          onChange={(e) => {
            changeFilterHandler('name', e.target.value);
          }}
          label={t('general.searchByName')}
          sx={{
            width: { xs: '100%', md: 'auto' },
            paddingBottom: { xs: 2, md: 0 },
          }}
        />

        {!isNormalUser && (
          <Button
            variant={'contained'}
            onClick={() => setIsAddModalOpened(true)}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            <AddCircleOutlineOutlinedIcon sx={{ marginRight: '5px' }} />
            {t('button.addNewClient')}
          </Button>
        )}
      </Box>
      <CustomDataGrid
        columns={columns}
        rows={data || []}
        rowCount={count}
        setPagination={setPagination}
        pagination={pagination}
        loading={data === undefined}
        hasNextPage={totalPages < pagination?.page}
      />

      <ConfirmDelete
        open={showDeleteModal}
        title={selectedClient?.name}
        handleClose={closeDeleteModal}
        handleSubmit={() => deleteHandler(selectedClient?.id)}
      />
      <ClientFormModal
        open={isAddModalOpened}
        isUpdate={!!selectedClient?.id}
        handleClose={closeDeleteModal}
        handleSubmit={handleSubmit}
      >
        <ClientFormModalBody
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
        />
      </ClientFormModal>
    </Card>
  );
};

export default AdminClients;
